import { useQuery } from "@apollo/client/react";
import { styled, Typography } from "@mui/material";

import { FilterLabel, FormGroupItem, LeftAlignDiv } from "../Filters";
import IconInfo from "./IconInfo";
import { QueryResult } from "./QueryResult";

import { useEffect } from "react";
import type {
  ExportDeclarationsQuery,
  ExportDeclarationsQueryVariables,
  PublishedSourceBundlesQuery,
  PublishedSourceBundlesQueryVariables,
} from "../../graphql/types";
import { ExportDeclarationsDocument, PublishedSourceBundlesDocument } from "../../graphql/types";
import { TreeBranch, TreeSelect } from "../TreeSelect/TreeSelect";

const PAGE_SIZE = 1000;

type PublishedSourceBundle = NonNullable<PublishedSourceBundlesQuery["source_bundles"]>["edges"][number]["node"];

type PublishedExportDeclaration = NonNullable<ExportDeclarationsQuery["export_declarations"]>["edges"][number]["node"];

const ProjectsContainer = styled("div")`
  position: relative;
`;

const sortByTitle = <T extends { title: string }>(a: T, b: T) => {
  if (a.title > b.title) return 1;
  if (a.title < b.title) return -1;
  return 0;
};

const PROJECT_PREFIX = "~//PROJECT//~:"; // Just in case a Project and ImportName have the same name

type SourceLeaf = {
  title: string;
  disabled: boolean;
  filterValue: ExportFilter | ImportFilter;
  destination?: string;
  show?: boolean;
};

type SourceNode = SourceLeaf & { children: SourceLeaf[] };

export const convertToTreeSelect = ({ nodes }: { nodes: SourceNode[] }): TreeBranch[] => {
  const result: TreeBranch[] = [];
  nodes.forEach((elem) => {
    const newBranch: TreeBranch[] = [];
    elem.children.forEach((child) => {
      const newBranchChild: TreeBranch = {
        id: child.title,
        label: child.title,
        branches: [],
        disabled: child.disabled,
        show: true,
        extraInfo: {
          filterValue: child.filterValue,
        },
        helperText: child.destination,
      };
      newBranch.push(newBranchChild);
    });
    const newNode: TreeBranch = {
      id: elem.title,
      label: elem.title,
      branches: newBranch,
      disabled: elem.disabled,
      show: true,
      extraInfo: {
        filterValue: elem.filterValue,
      },
    };
    result.push(newNode);
  });
  return result;
};

type ProjectAssetTreeSelectProps = {
  id: string;
  nodes: SourceNode[];
  selected: string | string[] | null;
  onTreeSelectChange: (value: TreeBranch[] | TreeBranch) => void;
  onTreeSelectClear?: () => void;
  multiple?: boolean;
};

export const ProjectAssetTreeSelect = ({
  id,
  nodes,
  selected,
  onTreeSelectChange,
  onTreeSelectClear,
  multiple = false,
}: ProjectAssetTreeSelectProps) => {
  const listMeta = convertToTreeSelect({ nodes: nodes });
  const values: TreeBranch[] = [];
  if (multiple && selected) {
    listMeta.forEach((branch) => {
      if ((branch.branches?.length || 0) > 0) {
        branch.branches?.forEach((elem) => {
          if ((elem.branches?.length || 0) > 0) {
            elem.branches?.forEach((subElem) => {
              if (selected.includes(subElem.label)) {
                values.push(subElem);
              }
            });
          } else {
            if (selected.includes(elem.label)) {
              values.push(elem);
            }
          }
        });
      }
      if (selected.includes(branch.label)) {
        values.push(branch);
      }
    });
  }
  return (
    <ProjectsContainer id={id}>
      <TreeSelect
        multiple={multiple}
        listMeta={listMeta}
        onSelectCallback={onTreeSelectChange}
        selectedValue={multiple ? values : selected || ""}
        onClear={onTreeSelectClear}
      />
    </ProjectsContainer>
  );
};

const EmptyAssetSelect = ({ id }: { id: string }) => {
  return (
    <ProjectAssetTreeSelect
      id={id}
      nodes={[]}
      selected={null}
      onTreeSelectChange={() => {}}
      onTreeSelectClear={() => null}
    />
  );
};

const ExplainPublishedImports = () => {
  const text = (
    <Typography variant="body.xSmall.regular">
      <span>Search by Project-Imports from the current Published Scripts.</span>
    </Typography>
  );
  return <IconInfo text={text} />;
};

export const toProjectImportNodes = (
  bundles: PublishedSourceBundle[],
  allowSelectProjects: boolean,
  allowSelectSuspended: boolean
) => {
  const nodesMap: { [key: string]: NodeMapEntryType<ImportFilter> } = {};
  bundles.forEach((bundle) => {
    bundle.tables.forEach((table) => {
      if (!table) return;
      const source = { ...table, status: bundle.status };
      const suspended =
        source.status !== "active" || (source.credential !== null && source.credential.status !== "active");
      const prefix = source.status !== "active" ? "import" : "key";
      const suffix = suspended ? ` (${prefix} suspended)` : "";
      const title = `${source.name}${suffix}`;
      const value = source.name;
      const disabled = suspended && !allowSelectSuspended;
      const filterValue = {
        project: null,
        importName: source.name,
        bundleName: bundle.bundle_name,
      };
      const option = {
        value,
        title,
        key: value,
        selectable: !disabled,
        disabled,
        filterValue,
        show: true,
      };

      if (nodesMap[source.project]) {
        nodesMap[source.project].children.push(option);
      } else {
        nodesMap[source.project] = {
          title: source.project,
          project: true,
          selectable: allowSelectProjects,
          disabled: !allowSelectProjects,
          value: `${PROJECT_PREFIX}${source.project}`,
          projectName: source.project,
          filterValue: { project: source.project, importName: null },
          children: [option],
          show: true,
        };
      }
    });
  });

  const nodes = Object.values(nodesMap).sort(sortByTitle);
  nodes.forEach((node) => node.children.sort(sortByTitle));
  return nodes;
};

type FilterPublishedImportsProps = {
  id: string;
  importName?: string | null;
  bundleName?: string | null;
  projectName?: string | null;
  onChange: (value: { importName?: string | null; project?: string | null; bundleName?: string | null }) => void;
  onLoaded?: () => void;
  filterByBackFill?: boolean;
  allowSelectProjects?: boolean;
  allowSelectSuspended?: boolean;
};

type PublishedImports = { rows: PublishedSourceBundle[] } & Pick<
  FilterPublishedImportsProps,
  | "id"
  | "importName"
  | "bundleName"
  | "projectName"
  | "onChange"
  | "allowSelectProjects"
  | "allowSelectSuspended"
  | "onChange"
>;

const PublishedImport = ({
  id,
  rows,
  allowSelectProjects = true,
  allowSelectSuspended = true,
  importName,
  bundleName,
  projectName,
  onChange,
}: PublishedImports) => {
  const nodes = toProjectImportNodes(rows, allowSelectProjects, allowSelectSuspended);
  // prettier-ignore
  const selectedValue =
    importName ? importName :
    bundleName ? bundleName :
    projectName ? projectName :
        null;
  return (
    <ProjectAssetTreeSelect
      id={id}
      nodes={nodes}
      selected={selectedValue}
      onTreeSelectChange={(value) => {
        if (Array.isArray(value)) {
          return;
        }
        const filterValue = value.extraInfo?.filterValue;
        if (filterValue) {
          onChange(filterValue);
        }
      }}
      onTreeSelectClear={() => {
        onChange({
          importName: null,
          project: null,
          bundleName: null,
        });
      }}
    />
  );
};

const FilterPublishedImports = ({
  id,
  importName,
  bundleName,
  projectName,
  onChange,
  onLoaded,
  filterByBackFill,
  allowSelectProjects = true,
  allowSelectSuspended = true,
}: FilterPublishedImportsProps) => {
  const variables = { first: PAGE_SIZE, can_backfill: filterByBackFill };
  const { loading, error, data } = useQuery<PublishedSourceBundlesQuery, PublishedSourceBundlesQueryVariables>(
    PublishedSourceBundlesDocument,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-and-network",
      variables,
    }
  );

  useEffect(() => {
    if (!loading && !error && data && onLoaded) {
      onLoaded();
    }
  }, [loading, error, data, onLoaded]);

  return (
    <>
      <FilterLabel className="form-control-title">Imports: {<ExplainPublishedImports />}</FilterLabel>
      <QueryResult<PublishedSourceBundle[], PublishedSourceBundlesQuery>
        emptyElement={<EmptyAssetSelect id={id} />}
        resultsMapper={(result) => (
          <PublishedImport
            rows={result}
            id={id}
            importName={importName}
            bundleName={bundleName}
            projectName={projectName}
            onChange={onChange}
            allowSelectProjects={allowSelectProjects}
            allowSelectSuspended={allowSelectSuspended}
          />
        )}
        loading={loading}
        error={error}
        data={data}
        nodeName="source_bundles"
        small={true}
      />
    </>
  );
};

type ImportFilter = {
  project: string | null;
  importName: string | null;
  bundleName?: string | null;
};

type ExportFilter = {
  project: string | null;
  exportName: string | null;
};

type NodeMapEntryType<T> = {
  title: string;
  project: boolean;
  selectable: boolean;
  disabled: boolean;
  value: string;
  projectName: string;
  filterValue: T;
  show?: boolean;
  children: {
    value: string;
    title: string;
    key: string;
    selectable: boolean;
    disabled: boolean;
    filterValue: T;
    show?: boolean;
  }[];
};

const toTreeExportNodes = (
  nodes: {
    project: string;
    status: string;
    name: string;
    credential?: { status: string } | null;
    destination?: string | null;
  }[],
  allowSelectProjects: boolean,
  allowSelectSuspended: boolean
) => {
  // The purpose of this function is to convert the data from the query into a format that the TreeSelect component can use
  // In this case we group the exports by project.
  const nodesMap: { [key: string]: NodeMapEntryType<ExportFilter> } = {};
  nodes.forEach((node) => {
    const suspended = node.status !== "active" || node.credential?.status !== "active";
    const prefix = node.status !== "active" ? "export" : "key";
    const suffix = suspended ? ` (${prefix} suspended)` : "";
    const title = `${node.name}${suffix}`;
    const value = node.name;
    const destination = node.destination;
    const disabled = suspended && !allowSelectSuspended;
    const option = {
      value,
      title,
      key: value,
      selectable: !disabled,
      disabled,
      filterValue: { project: "", exportName: node.name },
      destination,
      show: true,
    };

    if (nodesMap[node.project]) {
      nodesMap[node.project].children.push(option);
    } else {
      nodesMap[node.project] = {
        title: node.project,
        project: true,
        selectable: allowSelectProjects,
        disabled: !allowSelectProjects,
        value: `${PROJECT_PREFIX}${node.project}`,
        projectName: node.project,
        filterValue: { project: node.project, exportName: "" },
        children: [option],
        show: true,
      };
    }
  });

  const treeNodes = Object.values(nodesMap).sort(sortByTitle);
  treeNodes.forEach((node) => node.children.sort(sortByTitle));
  return treeNodes;
};

type PublishedExportsProps = {
  rows: PublishedExportDeclaration[];
  id: string;
  exportNames?: string[] | null;
  projectNames?: string[] | null;
  onChange: (value: TreeBranch[] | TreeBranch) => void;
  allowSelectProjects?: boolean;
  allowSelectSuspended?: boolean;
  multiple?: boolean;
};

const PublishedExport = ({
  rows,
  id,
  exportNames,
  projectNames,
  onChange,
  allowSelectProjects = true,
  allowSelectSuspended = true,
  multiple = false,
}: PublishedExportsProps) => {
  const nodes = toTreeExportNodes(rows, allowSelectProjects, allowSelectSuspended);
  const selectedValue: string[] = [];
  exportNames?.forEach((exportName) => {
    selectedValue.push(exportName);
  });
  projectNames?.forEach((project) => {
    selectedValue.push(project);
  });
  return (
    <ProjectAssetTreeSelect
      id={id}
      multiple={multiple}
      nodes={nodes}
      selected={selectedValue}
      onTreeSelectChange={onChange}
      onTreeSelectClear={() => {
        onChange([]);
      }}
    />
  );
};

const ExplainPublishedExports = () => {
  const text = (
    <LeftAlignDiv>
      <span>Search by Project-Exports from the current Published Scripts.</span>
    </LeftAlignDiv>
  );
  return <IconInfo text={text} />;
};

const FilterPublishedExports = ({
  id,
  exportNames,
  projectNames,
  onChange,
  allowSelectProjects = true,
  allowSelectSuspended = true,
  multiple = false,
}: Omit<PublishedExportsProps, "rows">) => {
  const { loading, error, data } = useQuery<ExportDeclarationsQuery, ExportDeclarationsQueryVariables>(
    ExportDeclarationsDocument,
    {
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-and-network",
      variables: { first: PAGE_SIZE },
    }
  );
  return (
    <>
      <FilterLabel className="form-control-title">Exports: {<ExplainPublishedExports />}</FilterLabel>
      <QueryResult<PublishedExportDeclaration[], ExportDeclarationsQuery>
        emptyElement={<EmptyAssetSelect id={id} />}
        resultsMapper={(rows) => (
          <PublishedExport
            id={id}
            rows={rows}
            exportNames={exportNames}
            projectNames={projectNames}
            onChange={onChange}
            allowSelectProjects={allowSelectProjects}
            allowSelectSuspended={allowSelectSuspended}
            multiple={multiple}
          />
        )}
        loading={loading}
        data={data}
        error={error}
        nodeName="export_declarations"
        small={true}
      />
    </>
  );
};

export {
  ExportFilter,
  FilterLabel,
  FilterPublishedExports,
  FilterPublishedImports,
  FormGroupItem,
  ImportFilter,
  LeftAlignDiv,
  NodeMapEntryType,
  SourceLeaf,
  SourceNode,
  toTreeExportNodes,
};
